import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Divider,
  FileInput,
  Group,
  List,
  Loader,
  Pagination,
  Progress,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconFileImport, IconSearch, IconUpload } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type {
  EtsngCodeRow,
  GngCodeRow,
  ReferenceBookId,
  ReferenceBookMetaDto,
  ReferenceCodeDto,
} from '@expeditor/shared';
import { referenceDataApi } from '../../shared/api/reference-data';
import { colIndex, parseGrid } from '../../shared/lib/csv';

/** Строк в одной пачке загрузки (тело запроса — небольшое). */
const CHUNK = 400;

type AnyRow = GngCodeRow | EtsngCodeRow;

interface BookConfig {
  id: ReferenceBookId;
  title: string;
  badge: string;
  formatHint: ReactNode;
  parse: (text: string) => AnyRow[];
  previewColumns: { key: string; label: string }[];
  importChunk: (rows: AnyRow[], replace: boolean, source?: string) => Promise<ReferenceBookMetaDto>;
}

/** Убирает дубли по коду (последняя выигрывает). */
/** Строк на страницу в админ-просмотре справочника. */
const PAGE_SIZE = 50;

function dedupeByCode(rows: AnyRow[]): AnyRow[] {
  const map = new Map<string, AnyRow>();
  for (const r of rows) map.set(r.code, r);
  return [...map.values()];
}

const BOOKS: Record<ReferenceBookId, BookConfig> = {
  gng: {
    id: 'gng',
    title: 'ГНГ — Гармонизированная номенклатура грузов',
    badge: 'ГНГ',
    formatHint: (
      <>
        <Text size="sm">
          Файл CSV/TSV. Колонки распознаются по заголовку официального файла ОСЖД
          (<Code>kod_gng, naimenovanie, klass_ett, pozitsiya, …, glava, …, razdel</Code>) либо по
          порядку: код, наименование, класс ЕТТ, позиция, …
        </Text>
      </>
    ),
    parse: (text) => {
      const { rows, header } = parseGrid(text);
      const iCode = colIndex(header, ['kod_gng', 'code', 'код', 'гнг'], 0);
      const iName = colIndex(header, ['naimenovanie', 'name', 'наименование'], 1);
      const iEtt = colIndex(header, ['klass_ett', 'ett', 'класс', 'класс_етт'], 2);
      const iPos = colIndex(header, ['pozitsiya', 'position', 'позиция'], 3);
      const iChap = colIndex(header, ['glava', 'chapter', 'глава'], 6);
      const iSect = colIndex(header, ['razdel', 'section', 'раздел'], 8);
      const out: GngCodeRow[] = [];
      for (const c of rows) {
        const code = (c[iCode] ?? '').trim();
        if (!code) continue;
        out.push({
          code,
          name: (c[iName] ?? '').trim().slice(0, 1000),
          ettClass: (c[iEtt] ?? '').trim() || null,
          position: (c[iPos] ?? '').trim() || null,
          chapter: (c[iChap] ?? '').trim() || null,
          section: (c[iSect] ?? '').trim() || null,
        });
      }
      return out;
    },
    previewColumns: [
      { key: 'code', label: 'Код' },
      { key: 'name', label: 'Наименование' },
      { key: 'ettClass', label: 'ЕТТ' },
      { key: 'position', label: 'Позиция' },
    ],
    importChunk: (rows, replace, source) =>
      referenceDataApi.importGng({ rows: rows as GngCodeRow[], replace, source }),
  },
  etsng: {
    id: 'etsng',
    title: 'ЕТСНГ — Единая тарифно-статистическая номенклатура грузов',
    badge: 'ЕТСНГ',
    formatHint: (
      <Text size="sm">
        Файл CSV/TSV. Распознаётся официальный справочник РЖД/Прейскуранта по заголовку
        (<Code>kod_etsng, naimenovanie, kratkoe_naimenovanie, tarifny_klass, mvn_tonn, …, opasny,
        skoroportyashchiysya</Code>) — тогда подтягиваются класс, МВН и признак опасности. Простой
        файл из двух колонок (<Code>код, наименование</Code>) тоже принимается.
      </Text>
    ),
    parse: (text) => {
      const { rows, header } = parseGrid(text);
      const isOfficial = !!header && header.some((h) => /kod_etsng|opasny/i.test(h));
      if (!isOfficial) {
        const iCode = colIndex(header, ['kod_etsng', 'kod', 'code', 'код', 'етснг'], 0);
        const iName = colIndex(header, ['naimenovanie', 'name', 'наименование'], 1);
        return rows
          .map((c) => ({ code: (c[iCode] ?? '').trim(), name: (c[iName] ?? '').trim().slice(0, 1000) }))
          .filter((r) => r.code);
      }
      const iCode = colIndex(header, ['kod_etsng', 'kod', 'code'], 0);
      const iName = colIndex(header, ['naimenovanie', 'name'], 2);
      const iShort = colIndex(header, ['kratkoe_naimenovanie', 'kratkoe'], 3);
      const iLevel = colIndex(header, ['uroven', 'level'], 1);
      const iClass = colIndex(header, ['tarifny_klass', 'klass'], 4);
      const iMvn = colIndex(header, ['mvn_tonn', 'mvn'], 5);
      const iSect = colIndex(header, ['razdel', 'section'], 11);
      const iDang = colIndex(header, ['opasny', 'опасный'], 12);
      const iPer = colIndex(header, ['skoroportyashchiysya', 'скоропортящийся'], 13);
      const out: EtsngCodeRow[] = [];
      for (const c of rows) {
        const code = (c[iCode] ?? '').trim();
        if (!code) continue;
        out.push({
          code,
          name: (c[iName] ?? '').trim().slice(0, 1000),
          shortName: (c[iShort] ?? '').trim() || null,
          level: (c[iLevel] ?? '').trim() || null,
          tariffClass: (c[iClass] ?? '').trim() || null,
          mvn: (c[iMvn] ?? '').trim() || null,
          dangerous: (c[iDang] ?? '').trim() === '1',
          perishable: (c[iPer] ?? '').trim() === '1',
          section: (c[iSect] ?? '').trim() || null,
        });
      }
      return out;
    },
    previewColumns: [
      { key: 'code', label: 'Код' },
      { key: 'name', label: 'Наименование' },
      { key: 'tariffClass', label: 'Класс' },
      { key: 'dangerous', label: 'Опасный' },
    ],
    importChunk: (rows, replace, source) =>
      referenceDataApi.importEtsng({ rows: rows as EtsngCodeRow[], replace, source }),
  },
};

/** Админ: просмотр/поиск и импорт одного справочника НСИ (ГНГ или ЕТСНГ). */
export function ReferenceBookPage() {
  const params = useParams();
  const book = (params.book === 'etsng' ? 'etsng' : 'gng') as ReferenceBookId;
  const cfg = BOOKS[book];

  const [meta, setMeta] = useState<ReferenceBookMetaDto | null>(null);
  const [text, setText] = useState('');
  const [source, setSource] = useState('');
  const [parsed, setParsed] = useState<AnyRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Просмотр текущего содержимого (поиск).
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ReferenceCodeDto[]>([]);
  const [searching, setSearching] = useState(false);
  // Постраничный просмотр: справочники большие (ГНГ — 11 558 кодов).
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setMeta(null);
    setText('');
    setParsed([]);
    setQuery('');
    setResults([]);
    setPage(1);
    setTotal(0);
    setOkMsg(null);
    setError(null);
    referenceDataApi
      .meta(book)
      .then(setMeta)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить сведения'));
  }, [book]);

  // Новый поиск — всегда с первой страницы.
  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    setSearching(true);
    const t = setTimeout(() => {
      referenceDataApi
        .list(book, query, page, PAGE_SIZE)
        .then((r) => {
          setResults(r.rows);
          setTotal(r.total);
        })
        .catch(() => {
          setResults([]);
          setTotal(0);
        })
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [book, query, page]);

  const onText = (value: string) => {
    setText(value);
    setParsed(dedupeByCode(cfg.parse(value)));
    setOkMsg(null);
  };

  const onFile = async (file: File | null) => {
    if (!file) return;
    onText(await file.text());
  };

  const doImport = async () => {
    if (parsed.length === 0) return;
    if (
      !window.confirm(
        `Загрузить ${parsed.length} строк в справочник «${cfg.badge}»? Текущее содержимое будет полностью заменено.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setOkMsg(null);
    setProgress({ done: 0, total: parsed.length });
    try {
      let last: ReferenceBookMetaDto | null = null;
      for (let i = 0; i < parsed.length; i += CHUNK) {
        const chunk = parsed.slice(i, i + CHUNK);
        last = await cfg.importChunk(chunk, i === 0, i === 0 ? source.trim() || undefined : undefined);
        setProgress({ done: Math.min(i + CHUNK, parsed.length), total: parsed.length });
      }
      if (last) setMeta(last);
      setOkMsg(`Загружено строк: ${last?.rowCount ?? parsed.length}.`);
      setText('');
      setParsed([]);
      setQuery('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка импорта');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const preview = parsed.slice(0, 5);
  const cellText = useMemo(
    () => (r: AnyRow, key: string) => {
      const v = (r as Record<string, unknown>)[key];
      if (typeof v === 'boolean') return v ? 'да' : '';
      return v == null ? '' : String(v);
    },
    [],
  );

  return (
    <Stack>
      <Anchor component={Link} to="/manager/reference-data" size="sm">
        <Group gap={4}>
          <IconArrowLeft size={14} /> Все справочники
        </Group>
      </Anchor>
      <Title order={2}>{cfg.title}</Title>

      {error && <Alert color="red">{error}</Alert>}
      {okMsg && <Alert color="teal">{okMsg}</Alert>}

      <Card withBorder padding="md">
        <Group justify="space-between">
          <Stack gap={2}>
            <Text fw={500}>Текущее состояние</Text>
            <Text size="sm" c="dimmed">
              Источник: {meta?.source ?? '—'}
            </Text>
            <Text size="sm" c="dimmed">
              Обновлён: {meta?.updatedAt ? new Date(meta.updatedAt).toLocaleString('ru-RU') : '—'}
            </Text>
          </Stack>
          <Badge size="lg" variant="light">
            {meta?.rowCount ?? 0} строк
          </Badge>
        </Group>
      </Card>

      <Divider label="Просмотр" labelPosition="left" />
      <TextInput
        leftSection={<IconSearch size={16} />}
        placeholder="Поиск по коду или наименованию"
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        rightSection={searching ? <Loader size="xs" /> : null}
      />
      {results.length > 0 ? (
        <>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {query.trim() ? `Найдено: ${total}` : `Всего строк: ${total}`}
              {total > PAGE_SIZE
                ? ` · страница ${page} из ${Math.ceil(total / PAGE_SIZE)}`
                : ''}
            </Text>
          </Group>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={120}>Код</Table.Th>
                <Table.Th>Наименование</Table.Th>
                <Table.Th w={160}>Доп.</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.map((r) => (
                <Table.Tr key={r.code}>
                  <Table.Td>{r.code}</Table.Td>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td>{r.detail ?? ''}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {total > PAGE_SIZE && (
            <Group justify="center">
              <Pagination
                value={page}
                onChange={setPage}
                total={Math.ceil(total / PAGE_SIZE)}
                siblings={1}
                withEdges
              />
            </Group>
          )}
        </>
      ) : (
        !searching && (
          <Text size="sm" c="dimmed">
            {query.trim() ? 'Ничего не найдено.' : 'Справочник пуст — загрузите данные ниже.'}
          </Text>
        )
      )}

      <Divider label="Импорт" labelPosition="left" />
      <Alert color="blue" variant="light">
        {cfg.formatHint}
        <List size="sm" mt={4}>
          <List.Item>Разделитель (таб/«;»/«,») и строка-заголовок определяются автоматически.</List.Item>
          <List.Item>Загрузка полностью заменяет текущий справочник.</List.Item>
          <List.Item>Обновляйте файл при выходе новых редакций номенклатуры.</List.Item>
        </List>
      </Alert>

      <FileInput
        label="Файл справочника (.csv / .tsv / .txt)"
        placeholder="Выберите файл"
        accept=".csv,.tsv,.txt"
        leftSection={<IconFileImport size={16} />}
        value={null}
        onChange={onFile}
        disabled={busy}
      />
      <Textarea
        label="…или вставьте содержимое"
        autosize
        minRows={3}
        maxRows={10}
        value={text}
        onChange={(e) => onText(e.currentTarget.value)}
        disabled={busy}
      />
      <TextInput
        label="Источник / версия (метка)"
        placeholder="Например: ОСЖД, ГНГ ред. 01.06.2026"
        value={source}
        onChange={(e) => setSource(e.currentTarget.value)}
        disabled={busy}
      />

      {parsed.length > 0 && (
        <Card withBorder padding="sm">
          <Text size="sm" mb={6}>
            Распознано строк: <b>{parsed.length}</b>. Предпросмотр первых {preview.length}:
          </Text>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                {cfg.previewColumns.map((c) => (
                  <Table.Th key={c.key}>{c.label}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {preview.map((r) => (
                <Table.Tr key={r.code}>
                  {cfg.previewColumns.map((c) => (
                    <Table.Td key={c.key}>{cellText(r, c.key)}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {progress && (
        <Stack gap={4}>
          <Progress value={(progress.done / progress.total) * 100} animated />
          <Text size="xs" c="dimmed">
            Загружено {progress.done} из {progress.total}…
          </Text>
        </Stack>
      )}

      <Group>
        <Button
          leftSection={<IconUpload size={16} />}
          onClick={doImport}
          loading={busy}
          disabled={parsed.length === 0}
        >
          Импортировать ({parsed.length})
        </Button>
      </Group>
    </Stack>
  );
}
